import React, { useEffect, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import buildUrl from '../../buildUrl';
import useApi from '../../hooks/useApi';
import { useTheme } from '@mui/material/styles';
import { Box, Button } from '@mui/material';
import usePrevious from '../../hooks/usePrevious';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { grey } from '@mui/material/colors';
import { Link } from 'react-router';

const rowsPerPageOptions = [10, 20, 50];

const calculateRowHeight = (message) => {
    const baseHeight = 44; // Base height of row
    const lineHeight = 24; // Line height for text
    const maxLines = 8; // Maximum lines before extra space is added
    const lines = Math.min(Math.ceil(message.length / 100), maxLines); // Approximation for text lines
    return baseHeight + (lineHeight * lines);
};

const SendHistory = ({ id, isReport }) => {
    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [rowHeight, setRowHeight] = useState(null);
    const [page, setPage] = useState(0);
    const [orderBy, setOrderBy] = useState('sent');
    const [orderDesc, setOrderDesc] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const prevRowsPerPage = usePrevious(rowsPerPage);
    const prevOrderBy = usePrevious(orderBy);
    const prevOrderDesc = usePrevious(orderDesc);
    const prevPage = usePrevious(page);
    const { handleGet } = useApi();
    const theme = useTheme();

    const columns = [
        {
            field: 'subject', headerName: 'Campaign', orderDesc: false, type: 'string', width: 300,
            renderCell: (params) => {
                const subject = params.row.subject || '';

                return (
                    <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        {subject}
                    </Box>
                );
            }
        },
        {
            field: 'shareLink', headerName: isReport ? 'Report' : 'Share Link', orderDesc: false, type: 'string', sortable: false,
            renderCell: (params) => isReport ? (
                <Button
                    disabled={params.row.sendStatus !== 'Sent'}
                    variant="text"
                    to={`/newreports/emails/${params.row.campaignId}`}
                    component={Link}
                >
                    View
                </Button>
            ) : (
                <Button
                    disabled={params.row.sendStatus !== 'Sent'}
                    variant="text"
                    href={params.row.shareLink}
                    target="_blank"
                >
                    View
                </Button>
            )
        },
        { field: 'sendStatus', headerName: 'Status', orderDesc: false, type: 'string' },
        { field: 'sent', headerName: 'Send Date', orderDesc: true, type: 'dateTime', width: 160, valueGetter: value => value && new Date(value) },
        { field: 'opened', headerName: 'Opened', orderDesc: false, type: 'boolean' },
        { field: 'clicked', headerName: 'Clicked', orderDesc: false, type: 'boolean' },
        { field: 'shared', headerName: 'Shared', orderDesc: false, type: 'boolean' },
        { field: 'bounced', headerName: 'Bounced', orderDesc: false, type: 'boolean' },
        {
            field: 'bounceMessage', headerName: 'Bounce Message', orderDesc: false, type: 'string', width: 600,
            renderCell: (params) => {
                const message = params.row.bounceMessage || '';

                return message ? (
                    <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        {message}
                    </Box>
                ) : <ClearOutlinedIcon sx={{ fontSize: 20, color: grey[500] }} />;
            }
        }
    ];

    const handleFetchContactSendHistory = async () => {
        const params = {
            skip: page * rowsPerPage,
            take: rowsPerPage,
            field: orderBy,
            isDescending: orderDesc
        };

        const url = buildUrl(`contacts/${id}/campaigns`, params);

        const response = await handleGet(url);

        if (!response.ok) {
            setIsLoading(false);
            console.error(response?.error);
            return;
        }

        const data = await response.json();

        setData(data);
        setIsLoading(false);
    };

    const handleFetchContactSendHistoryCount = async () => {
        const response = await handleGet(`contacts/${id}/campaigns/count`);

        if (!response.ok) {
            console.error(response?.error);
            return;
        }

        const count = await response.json();

        setCount(count);
    };

    const handleSort = props => {
        const { field, sort } = props[0];

        if (field === orderBy) {
            setOrderDesc(!orderDesc);
        }
        else {
            setOrderBy(field);
            setOrderDesc(sort === 'desc');
        }
    };

    useEffect(() => {
        if (orderBy !== prevOrderBy ||
            orderDesc !== prevOrderDesc ||
            rowsPerPage !== prevRowsPerPage) {
            setPage(0);
            setIsLoading(true);
        }
        else if (page !== prevPage) {
            setIsLoading(true);
        }
    }, [orderBy, orderDesc, rowsPerPage, page]);

    useEffect(() => {
        // Update row height dynamically based on the longest message
        const maxMessage = data.reduce((max, row) => row.bounceMessage && row.bounceMessage.length > max.length ? row.bounceMessage : max, '');

        setRowHeight(calculateRowHeight(maxMessage));
    }, [data]);

    useEffect(() => {
        if (isLoading) {
            handleFetchContactSendHistory();
            handleFetchContactSendHistoryCount();
        }
    }, [isLoading]);

    const sort = orderDesc ? 'desc' : 'asc';

    return (
        <Box
            sx={{
                '& .MuiDataGrid-booleanCell[data-value="true"]': {
                    color: theme.palette.success.light
                },
                '& .MuiDataGrid-booleanCell[data-value="false"]': {
                    color: theme.palette.error.light
                }
            }}
        >
            <DataGridPro
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnFilter
                disableMultipleColumnsSorting
                hideFooterSelectedRowCount
                pagination
                paginationMode="server"
                sortingMode="server"
                rows={data}
                rowCount={count}
                rowHeight={rowHeight ?? undefined}
                columns={columns}
                loading={isLoading}
                pageSizeOptions={rowsPerPageOptions}
                paginationModel={{ page, pageSize: rowsPerPage }}
                onPaginationModelChange={(paginationModel) => {
                    setRowsPerPage(paginationModel.pageSize);
                    setPage(paginationModel.page);
                }}
                onSortModelChange={props => handleSort(props)}
                sortingOrder={['desc', 'asc']}
                initialState={{
                    sorting: {
                        sortModel: [{ field: orderBy, sort }]
                    }
                }}
            />
        </Box>
    );
};

export default SendHistory;